import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Plus, PiggyBank, AlertTriangle, Check, Trash2, Users, User, PieChart, Edit2 } from 'lucide-react';
import BottomNav from '../components/BottomNav';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { getBudgets, createBudget, deleteBudget, getFamily } from '../lib/api';
import { formatCurrency, getCategoryColor } from '../lib/utils';
import { useTutorial } from '../components/OnboardingTutorial';

const OnboardingTutorial = lazy(() => import('../components/OnboardingTutorial'));

// Budget Card Component
const BudgetCard = ({ budget, index, t, language, getCategoryEmoji, handleDelete, handleEdit, badgeType }) => {
  const isOverBudget = budget.percentage > 100;
  const isWarning = budget.percentage > 80 && budget.percentage <= 100;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
    >
      <Card data-testid={`budget-card-${budget.category}`}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div 
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${getCategoryColor(budget.category)}20` }}
              >
                <span className="text-xl">{getCategoryEmoji(budget.category)}</span>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-medium">{t(`cat_${(budget.category || 'autre').toLowerCase()}`)}</h3>
                  {badgeType === 'common' && (
                    <span className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 px-1.5 py-0.5 rounded-full">
                      {t('common')}
                    </span>
                  )}
                  {badgeType === 'personal' && (
                    <span className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 px-1.5 py-0.5 rounded-full">
                      {t('personal')}
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  {formatCurrency(budget.spent)} / {formatCurrency(budget.amount)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-sm font-medium ${
                isOverBudget ? 'text-red-500' :
                isWarning ? 'text-orange-500' :
                'text-green-500'
              }`}>
                {Math.round(budget.percentage)}%
              </span>
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => handleEdit(budget)}
                className="text-muted-foreground hover:text-primary"
                data-testid={`edit-budget-${budget.category}`}
              >
                <Edit2 className="w-4 h-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => handleDelete(budget.category)}
                className="text-muted-foreground hover:text-destructive"
                data-testid={`delete-budget-${budget.category}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full transition-all duration-500"
              style={{
                width: `${Math.min(100, budget.percentage)}%`,
                backgroundColor: isOverBudget ? '#EF4444' : 
                               isWarning ? '#F59E0B' : 
                               getCategoryColor(budget.category)
              }}
            />
          </div>
          {budget.remaining < 0 ? (
            <p className="text-sm text-red-500 mt-2 flex items-center gap-1">
              <AlertTriangle className="w-4 h-4" />
              {t('over_by')} {formatCurrency(Math.abs(budget.remaining))}
            </p>
          ) : (
            <p className="text-sm text-muted-foreground mt-2">
              {t('budget_remaining')}: {formatCurrency(budget.remaining)}
            </p>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

const CATEGORIES = [
  { id: 'logement', emoji: '🏠' },
  { id: 'transport', emoji: '🚗' },
  { id: 'alimentation', emoji: '🍔' },
  { id: 'abonnements', emoji: '📱' },
  { id: 'shopping', emoji: '🛍️' },
  { id: 'loisirs', emoji: '🎮' },
  { id: 'sante', emoji: '💊' },
  { id: 'epargne', emoji: '💰' },
  { id: 'education', emoji: '🎓' },
  { id: 'animaux', emoji: '🐾' },
  { id: 'enfants', emoji: '👶' },
  { id: 'cadeaux', emoji: '🎁' },
  { id: 'entretien', emoji: '🔧' },
  { id: 'professionnel', emoji: '💼' },
  { id: 'divers', emoji: '📦' },
];

export default function BudgetsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const { shouldShow: showTutorial } = useTutorial();
  
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [family, setFamily] = useState(null);
  const [tutorialActive, setTutorialActive] = useState(false);
  const [editingBudget, setEditingBudget] = useState(null);  // null = create mode, budget object = edit mode
  
  const [newBudget, setNewBudget] = useState({
    category: '',
    amount: '',
    budget_type: 'personal', // personal or common
  });

  useEffect(() => {
    loadBudgets();
    loadFamily();
  }, []);

  // Show tutorial after component is mounted
  useEffect(() => {
    if (showTutorial) {
      const timer = setTimeout(() => setTutorialActive(true), 500);
      return () => clearTimeout(timer);
    }
  }, [showTutorial]);

  const loadFamily = async () => {
    try {
      const res = await getFamily();
      setFamily(res.data.family);
    } catch (e) {
      // No family
    }
  };

  const loadBudgets = async () => {
    try {
      setLoading(true);
      const response = await getBudgets();
      setBudgets(response.data);
    } catch (error) {
      console.error('Failed to load budgets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      await createBudget({
        category: newBudget.category,
        amount: parseFloat(newBudget.amount),
        budget_type: family?.budget_mode === 'hybrid' ? newBudget.budget_type : 'personal',
      });
      setShowCreate(false);
      setEditingBudget(null);
      setNewBudget({ category: '', amount: '', budget_type: 'personal' });
      loadBudgets();
    } catch (error) {
      console.error('Failed to create budget:', error);
    }
  };

  const handleEdit = (budget) => {
    setEditingBudget(budget);
    setNewBudget({
      category: budget.category,
      amount: String(budget.amount ?? ''),
      budget_type: budget.budget_type || 'personal',
    });
    setShowCreate(true);
  };

  const handleCloseDialog = (open) => {
    setShowCreate(open);
    if (!open) {
      setEditingBudget(null);
      setNewBudget({ category: '', amount: '', budget_type: 'personal' });
    }
  };

  const handleDelete = async (category) => {
    if (!window.confirm(t('delete_budget_confirm'))) {
      return;
    }
    try {
      await deleteBudget(category);
      loadBudgets();
    } catch (error) {
      console.error('Failed to delete budget:', error);
    }
  };

  // Calculate totals
  const totalBudget = budgets.reduce((sum, b) => sum + b.amount, 0);
  const totalSpent = budgets.reduce((sum, b) => sum + b.spent, 0);
  const overallPercentage = totalBudget > 0 ? (totalSpent / totalBudget) * 100 : 0;

  // Separate budgets by type for hybrid mode
  const commonBudgets = budgets.filter(b => b.budget_type === 'common');
  const personalBudgets = budgets.filter(b => b.budget_type !== 'common');
  const isHybridMode = family?.budget_mode === 'hybrid';

  // Categories not yet budgeted
  const budgetedCategories = budgets.map(b => b.category);
  const availableCategories = CATEGORIES.filter(c => !budgetedCategories.includes(c.id));

  const getCategoryEmoji = (category) => {
    const cat = CATEGORIES.find(c => c.id === category);
    return cat?.emoji || '📦';
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-3xl mx-auto px-4 py-4 space-y-4">
          {/* Title row */}
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-bold">{t('budgets')}</h1>
          </div>
          {/* Actions row */}
          <div className="flex items-center justify-end gap-2">
            <Dialog open={showCreate} onOpenChange={handleCloseDialog}>
              <DialogTrigger asChild>
                <Button disabled={availableCategories.length === 0} data-testid="add-budget-btn" className="whitespace-nowrap">
                  <Plus className="w-4 h-4 mr-2" />
                  {t('add')}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingBudget ? t('edit_budget') : t('set_budget')}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">{t('category')}</label>
                    <Select 
                      value={newBudget.category} 
                      onValueChange={(value) => setNewBudget(prev => ({ ...prev, category: value }))}
                      disabled={!!editingBudget}
                    >
                      <SelectTrigger data-testid="budget-category-select">
                        <SelectValue placeholder={t('choose_category')}>
                          {editingBudget ? (
                            <span className="flex items-center gap-2">
                              <span>{getCategoryEmoji(newBudget.category)}</span>
                              <span>{t(`cat_${newBudget.category}`)}</span>
                            </span>
                          ) : null}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {availableCategories.map(cat => (
                          <SelectItem key={cat.id} value={cat.id}>
                            <span className="flex items-center gap-2">
                              <span>{cat.emoji}</span>
                              <span>{t(`cat_${cat.id}`)}</span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">{t('amount')} (€)</label>
                    <Input
                      type="number"
                      placeholder="200"
                      value={newBudget.amount}
                      onChange={(e) => setNewBudget(prev => ({ ...prev, amount: e.target.value }))}
                      data-testid="budget-amount-input"
                    />
                  </div>
                  {/* Budget type selector for Hybrid mode */}
                  {isHybridMode && (
                    <div>
                      <label className="text-sm font-medium mb-2 block">
                        {t('budget_type')}
                      </label>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant={newBudget.budget_type === 'personal' ? 'default' : 'outline'}
                          className="flex-1 gap-2"
                          onClick={() => setNewBudget(prev => ({ ...prev, budget_type: 'personal' }))}
                        >
                          <User className="w-4 h-4" />
                          {t('personal')}
                        </Button>
                        <Button
                          type="button"
                          variant={newBudget.budget_type === 'common' ? 'default' : 'outline'}
                          className="flex-1 gap-2"
                          onClick={() => setNewBudget(prev => ({ ...prev, budget_type: 'common' }))}
                        >
                          <Users className="w-4 h-4" />
                          {t('common')}
                        </Button>
                      </div>
                    </div>
                  )}
                  <Button 
                    onClick={handleCreate} 
                    className="w-full"
                    disabled={!newBudget.category || !newBudget.amount}
                    data-testid="save-budget-btn"
                  >
                    {editingBudget ? t('save_changes') : t('save')}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 py-6">
        {/* Overall Progress */}
        {budgets.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <Card className="bg-gradient-to-br from-card to-muted/30" data-testid="overall-budget-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">
                      {t('total_budget_month')}
                    </p>
                    <h2 className="text-3xl font-bold">{formatCurrency(totalBudget)}</h2>
                  </div>
                  <div className={`px-4 py-2 rounded-full text-sm font-medium ${
                    overallPercentage > 100 ? 'bg-red-100 text-red-600' :
                    overallPercentage > 80 ? 'bg-orange-100 text-orange-600' :
                    'bg-green-100 text-green-600'
                  }`}>
                    {overallPercentage > 100 ? (
                      <span className="flex items-center gap-1">
                        <AlertTriangle className="w-4 h-4" />
                        {t('over_budget')}
                      </span>
                    ) : (
                      <span className="flex items-center gap-1">
                        <Check className="w-4 h-4" />
                        {Math.round(overallPercentage)}% {t('used')}
                      </span>
                    )}
                  </div>
                </div>
                <Progress 
                  value={Math.min(100, overallPercentage)} 
                  className="h-3"
                />
                <div className="flex justify-between text-sm mt-2">
                  <span className="text-muted-foreground">
                    {t('budget_spent')}: {formatCurrency(totalSpent)}
                  </span>
                  <span className="text-muted-foreground">
                    {t('budget_remaining')}: {formatCurrency(Math.max(0, totalBudget - totalSpent))}
                  </span>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Budgets List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto" />
          </div>
        ) : budgets.length > 0 ? (
          <>
            {/* Hybrid Mode: Separate sections */}
            {isHybridMode ? (
              <>
                {/* Common Budgets Section */}
                {commonBudgets.length > 0 && (
                  <div className="mb-8">
                    <div className="flex items-center gap-2 mb-4">
                      <Users className="w-5 h-5 text-green-600" />
                      <h2 className="text-lg font-semibold text-green-600">
                        {t('common_budgets')}
                      </h2>
                      <span className="text-sm text-muted-foreground">
                        ({t('from_common_pot')})
                      </span>
                    </div>
                    <div className="space-y-4">
                      {commonBudgets.map((budget, index) => (
                        <BudgetCard 
                          key={budget.budget_id} 
                          budget={budget} 
                          index={index}
                          t={t}
                          language={language}
                          getCategoryEmoji={getCategoryEmoji}
                          handleDelete={handleDelete}
                          handleEdit={handleEdit}
                          badgeType="common"
                        />
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Personal Budgets Section */}
                {personalBudgets.length > 0 && (
                  <div className="mb-8">
                    <div className="flex items-center gap-2 mb-4">
                      <User className="w-5 h-5 text-blue-600" />
                      <h2 className="text-lg font-semibold text-blue-600">
                        {t('personal_budgets')}
                      </h2>
                      <span className="text-sm text-muted-foreground">
                        ({t('from_your_budget')})
                      </span>
                    </div>
                    <div className="space-y-4">
                      {personalBudgets.map((budget, index) => (
                        <BudgetCard 
                          key={budget.budget_id} 
                          budget={budget} 
                          index={index}
                          t={t}
                          language={language}
                          getCategoryEmoji={getCategoryEmoji}
                          handleDelete={handleDelete}
                          handleEdit={handleEdit}
                          badgeType="personal"
                        />
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              /* Standard list for non-hybrid modes */
              <div className="space-y-4">
                {budgets.map((budget, index) => (
                  <BudgetCard 
                    key={budget.budget_id} 
                    budget={budget} 
                    index={index}
                    t={t}
                    language={language}
                    getCategoryEmoji={getCategoryEmoji}
                    handleDelete={handleDelete}
                    handleEdit={handleEdit}
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
              <PiggyBank className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="font-medium mb-2">
              {t('no_budgets_configured')}
            </h3>
            <p className="text-muted-foreground mb-6">
              {language === 'fr' 
                ? 'Configure tes budgets pour suivre tes dépenses !'
                : language === 'pt'
                ? 'Configura os teus orçamentos para acompanhar as despesas!'
                : 'Set up your budgets to track your expenses!'
              }
            </p>
            <Button onClick={() => setShowCreate(true)} data-testid="create-first-budget-btn">
              <Plus className="w-4 h-4 mr-2" />
              {t('set_budget')}
            </Button>
          </div>
        )}

        {/* Empty state with explanation */}
        {budgets.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-12"
          >
            <div className="w-20 h-20 mx-auto mb-4 bg-primary/10 rounded-full flex items-center justify-center">
              <PieChart className="w-10 h-10 text-primary" />
            </div>
            <h3 className="text-lg font-semibold mb-2">{t('no_budgets_yet')}</h3>
            <p className="text-muted-foreground text-sm max-w-md mx-auto mb-6">
              {t('no_budgets_explanation')}
            </p>
            <Button onClick={() => setShowCreate(true)} data-testid="add-first-budget-btn">
              <Plus className="w-4 h-4 mr-2" />
              {t('add_first_budget')}
            </Button>
          </motion.div>
        )}

        {/* Suggested budgets based on salary */}
        {budgets.length === 0 && user?.salary && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-8"
          >
            <h3 className="font-medium mb-4">
              {t('suggested_budget')}
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {[
                { id: 'logement', percent: 30 },
                { id: 'alimentation', percent: 15 },
                { id: 'transport', percent: 10 },
                { id: 'epargne', percent: 20 },
                { id: 'loisirs', percent: 10 },
                { id: 'abonnements', percent: 5 },
              ].map(suggestion => (
                <Card 
                  key={suggestion.id}
                  className="cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => {
                    setNewBudget({
                      category: suggestion.id,
                      amount: Math.round(user.salary * suggestion.percent / 100).toString()
                    });
                    setShowCreate(true);
                  }}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{getCategoryEmoji(suggestion.id)}</span>
                      <div>
                        <p className="font-medium text-sm">{t(`cat_${suggestion.id}`)}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatCurrency(Math.round(user.salary * suggestion.percent / 100))}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </motion.div>
        )}
      </main>

      {/* Onboarding Tutorial */}
      <Suspense fallback={null}>
        {tutorialActive && (
          <OnboardingTutorial onComplete={() => setTutorialActive(false)} />
        )}
      </Suspense>

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
}
